import { useState } from "react";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../Redux/Store/Store";
import {
  selectCriteriaById,
  UpdateFieldContent,
  UpdateFieldContentPayload,
  selectRolePermissions,
  CheckFieldChanged,
  selectGenerateAnswersProgressSteps,
} from "../../Redux/Store/Slices/questionnaire";
import layout from "../../Modules/layout.module.css";
import styles from "./QuestionSectionCriteria.module.css";
import { FieldType, IDynamicField } from "../../Types/Products/IDynamicField";
import { fieldsInExportOrder } from "../../Utils/Export/exporters/exportHelpers";
import Form from "../Utils/Form/Form";
import FloatingLabelTextArea from "../Utils/Form/FloatingLabelTextArea/FloatingLabelTextArea";
import Tooltip from "../Utils/Tooltip/Tooltip";
import AiInsightsBanner from "../Utils/AiInsightsBanner/AiInsightsBanner";
import CurateResourceTiles from "../Analyse/Formats/CurateResourceTiles";
import ResourceViewer from "../Utils/ResourceViewer/ResourceViewer";
import { CurateResource } from "../../Types/Curate/CurateResource";
import { getClient } from "../../Redux/Store/Slices/client";

type QuestionSectionResultsProps = {
  completed: boolean;
  loading: boolean;
  showAiInsights?: boolean;
  selectedFiles?: Array<{ id: string; fileName: string }>;
};

const QuestionSectionResults = ({
  loading,
  showAiInsights = false,
  selectedFiles = [],
}: QuestionSectionResultsProps) => {
  const dispatch = useAppDispatch();
  const { areaId } = useParams();
  const { activeCriteriaId, activeSectionId, recommendedCurateResources } = useAppSelector((state) => state.questionnaire);
  const client = useAppSelector(getClient);
  const progressSteps = useAppSelector(selectGenerateAnswersProgressSteps);
  const [viewerResource, setViewerResource] = useState<CurateResource | null>(null);

  const criteria = useAppSelector(selectCriteriaById(activeSectionId, activeCriteriaId));
  const rolePermissions = useAppSelector(selectRolePermissions());

  const handleInputChange = (field: IDynamicField, value: string) => {
    if (criteria && areaId && field) {
      const newField = { ...field, content: value };
      dispatch(
        UpdateFieldContent({
          field: newField,
          activityId: areaId,
          criteriaId: criteria.id,
        } as UpdateFieldContentPayload)
      );
    }
  };

  const checkFieldChangedInternal = async (field: IDynamicField) => {
    dispatch(CheckFieldChanged(field.id));
  };

  const steps =
    progressSteps.length > 0 ? progressSteps : ["Preparing assessment..."];

  return (
    <section className={styles.column2}>
      <div className={styles.resultsPanel}>
        <Form>
          {loading ? (
            <div className={styles.generateProgressPanel} role="status" aria-live="polite">
              <div className={styles.generateProgressHeader}>
                <div className={styles.generateProgressOrb}>
                  <span className={styles.generateProgressOrbPulse} aria-hidden="true" />
                  <span className={styles.generateProgressOrbRing} aria-hidden="true" />
                  <img
                    className={styles.generateProgressOrbLogo}
                    src="/img/logo.png"
                    alt=""
                  />
                </div>
                <div className={styles.generateProgressBody}>
                  <span className={styles.generateProgressTitle}>
                    Mia is working on your summary
                  </span>
                  <div className={styles.generateProgressTrack}>
                    <div className={styles.generateProgressBar} />
                  </div>
                </div>
              </div>
              <ol className={styles.generateProgressSteps}>
                {steps.map((step, index) => {
                  const isActive = index === steps.length - 1;
                  return (
                    <li
                      key={`${index}-${step}`}
                      className={`${styles.generateProgressStep} ${
                        isActive
                          ? styles.generateProgressStepActive
                          : styles.generateProgressStepDone
                      }`}
                    >
                      <span className={styles.generateStepMarker}>
                        {isActive ? (
                          <span className={styles.generateStepSpinner} />
                        ) : (
                          <svg
                            viewBox="0 0 12 12"
                            className={styles.generateStepCheck}
                            aria-hidden="true"
                          >
                            <path
                              d="M2.5 6.5 L5 9 L9.5 3.5"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="1.8"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                      </span>
                      <span className={styles.generateStepText}>{step}</span>
                    </li>
                  );
                })}
              </ol>
            </div>
          ) : (
            <div className={styles.resultsFields}>
              {fieldsInExportOrder(criteria?.fields).map((field) => {
                return (
                  <Tooltip
                    key={field.id}
                    align="top"
                    text={`Your role as a ${rolePermissions?.roleTitle} means that you cannot edit this text.`}
                    disabled={rolePermissions?.editGeneratedContent}
                  >
                    <div className={layout.column1}>
                      {field.type === FieldType.Text && (
                        <FloatingLabelTextArea
                          useInternalValue={true}
                          key={`${field.id}`}
                          onFocus={() => checkFieldChangedInternal(field)}
                          suspended={field.suspended}
                          readonly={!rolePermissions?.editGeneratedContent || field.locked}
                          label={field.title}
                          onChange={(value) => handleInputChange(field, value)}
                          value={field.content || ""}
                          placeholder={field.placeholder || ""}
                        />
                      )}
                    </div>
                  </Tooltip>
                );
              })}

              <CurateResourceTiles
                resources={
                  criteria?.recommendedCurateResources ??
                  recommendedCurateResources ??
                  []
                }
                resourceClicked={setViewerResource}
              />
            </div>
          )}
          {showAiInsights && (
            <div className={styles.resultsBannerFooter}>
              <AiInsightsBanner selectedFiles={selectedFiles} variant="footer" />
            </div>
          )}
        </Form>
      </div>
      <ResourceViewer
        resource={viewerResource}
        clientId={client?.id ?? ""}
        onClose={() => setViewerResource(null)}
      />
    </section>
  );
};

export default QuestionSectionResults;
